import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Carousel = ({ fitMode = 'contain' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [carouselImages, setCarouselImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar imágenes desde JSON
  useEffect(() => {
    const loadCarouselData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/data/carousel.json?v=${Date.now()}`);
        if (!response.ok) {
          throw new Error('Error al cargar las imágenes del carousel');
        }
        const data = await response.json();
        
        // Obtener slides del nuevo formato (si existe) o usar el formato antiguo
        const slides = data.slides || data;
        
        // Filtrar imágenes por visibilidad y fechas
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0); // Resetear horas para comparar solo fechas
        
        const imagenesVisibles = slides.filter(imagen => {
          // Verificar que esté marcada como visible
          if (!imagen.visible) return false;
          
          // Verificar fechas si están definidas
          if (imagen.fechaInicio && imagen.fechaFinal) {
            const inicio = new Date(imagen.fechaInicio);
            const final = new Date(imagen.fechaFinal);
            inicio.setHours(0, 0, 0, 0);
            final.setHours(23, 59, 59, 999); // Incluir todo el día final
            
            return hoy >= inicio && hoy <= final;
          }
          
          // Si no tiene fechas definidas, mostrarla si es visible
          return true;
        });
        
        setCarouselImages(imagenesVisibles);
        setError(null);
      } catch (err) {
        console.error('Error cargando carousel:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadCarouselData();
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  }, [carouselImages.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
    );
  }, [carouselImages.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, goToNext]);

  // Mostrar estado de carga
  if (isLoading) {
    return (
      <div className="relative w-full h-[280px] sm:h-[400px] md:h-[500px] lg:h-[85vh] xl:h-[85vh] 2xl:h-[90vh] bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0b742f] mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando imágenes...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si falla la carga
  if (error) {
    return (
      <div className="relative w-full h-[280px] sm:h-[400px] md:h-[500px] lg:h-[85vh] xl:h-[85vh] 2xl:h-[90vh] bg-red-50 flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 font-semibold mb-2">Error al cargar el carousel</p>
          <p className="text-red-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // No mostrar nada si no hay imágenes
  if (!carouselImages || carouselImages.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full h-[280px] sm:h-[400px] md:h-[500px] lg:h-[85vh] xl:h-[85vh] 2xl:h-[90vh] bg-white overflow-hidden group">
      {/* Carousel Images */}
      <div className="relative w-full h-full flex items-center justify-center">
        {carouselImages.map((image, index) => (
          <div
            key={image.id}
            className={`absolute top-0 left-0 w-full h-full flex items-center justify-center transition-all duration-700 ease-in-out ${index === currentIndex
              ? 'opacity-100 scale-100'
              : 'opacity-0 scale-105'
              }`}
          >
            <img
              src={image.url}
              alt={image.alt}
              className={`w-full h-full ${fitMode === 'contain'
                ? 'object-contain'
                : fitMode === 'cover'
                  ? 'object-cover object-center'
                  : 'object-fill'
                }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={() => {
          goToPrevious();
          setIsAutoPlaying(false);
        }}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 p-2 sm:p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-4 h-4 sm:w-6 sm:h-6" />
      </button>

      <button
        onClick={() => {
          goToNext();
          setIsAutoPlaying(false);
        }}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 p-2 sm:p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
        aria-label="Next slide"
      >
        <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
      </button>

      {/* Dots Indicators */}
      <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-2">
        {carouselImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${index === currentIndex
              ? 'bg-white w-6 sm:w-8 h-1.5 sm:h-2'
              : 'bg-white/50 hover:bg-white/75 w-1.5 sm:w-2 h-1.5 sm:h-2'
              }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;